import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const FORMATS = [
  { id: "both", label: "PDF + Word" },
  { id: "pdf", label: "Solo PDF" },
  { id: "docx", label: "Solo Word" },
];

function NeoInput(props) {
  return (
    <input
      {...props}
      className={`w-full h-11 px-3 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow ${props.className || ""}`}
    />
  );
}
function NeoTextarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-3 py-2 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow ${props.className || ""}`}
    />
  );
}
function NeoSelect({ options, ...props }) {
  return (
    <select
      {...props}
      className={`w-full h-11 px-3 border-2 border-[#1C1917] bg-white text-[#1C1917] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow ${props.className || ""}`}
    >
      {options.map((o) => (
        <option key={o.value || o} value={o.value || o}>{o.label || o}</option>
      ))}
    </select>
  );
}

function Field({ q, value, onChange }) {
  const common = {
    name: q.key,
    value: value || "",
    onChange: (e) => onChange(q.key, e.target.value),
    placeholder: q.placeholder || "",
    "data-testid": `wiz-q-${q.key}`,
    required: !!q.required,
  };
  if (q.type === "textarea") return <NeoTextarea rows={3} {...common} />;
  if (q.type === "date") return <NeoInput type="date" {...common} />;
  if (q.type === "number") return <NeoInput type="number" {...common} />;
  if (q.type === "select") {
    return (
      <NeoSelect
        {...common}
        options={[{ value: "", label: "— Selecciona —" }, ...(q.options || []).map((o) => ({ value: o, label: o }))]}
      />
    );
  }
  return <NeoInput type="text" {...common} />;
}

function evalShowIf(condition, answers) {
  if (!condition) return true;
  const v = answers[condition.key];
  if ("equals" in condition) return v === condition.equals;
  if ("in" in condition) return (condition.in || []).includes(v);
  if ("not_equals" in condition) return v !== condition.not_equals;
  return true;
}

export default function Wizard() {
  const { wid } = useParams();
  const navigate = useNavigate();
  const [wiz, setWiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [stepIdx, setStepIdx] = useState(0);
  const [outputFormat, setOutputFormat] = useState("both");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/docgen/wizards/${wid}`).then((r) => setWiz(r.data)).catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    });
  }, [wid]);

  const visibleSteps = useMemo(() => {
    if (!wiz) return [];
    return wiz.steps.filter((s) => evalShowIf(s.show_if, answers));
  }, [wiz, answers]);

  const currentStep = visibleSteps[stepIdx];
  const isReview = stepIdx >= visibleSteps.length;

  function setAnswer(k, v) {
    setAnswers((prev) => ({ ...prev, [k]: v }));
  }

  function next() {
    // Validate required of current step
    if (currentStep) {
      const missing = currentStep.questions.find(
        (q) => q.required && !String(answers[q.key] || "").trim(),
      );
      if (missing) {
        toast.error(`Completa: ${missing.label}`);
        return;
      }
    }
    setStepIdx((i) => i + 1);
  }
  function prev() {
    setStepIdx((i) => Math.max(0, i - 1));
  }

  async function generate() {
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/docgen/generate", {
        mode: "wizard",
        wizard_id: wiz.id,
        variables: answers,
        output_format: outputFormat,
      });
      setResult(data);
      toast.success("Documento generado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  }

  if (!wiz) return null;

  const progress = visibleSteps.length === 0 ? 100 : Math.round(((stepIdx) / visibleSteps.length) * 100);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]" data-testid="wizard-page">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12">
        <Link to="/tools/docgen" className="eyebrow text-xs text-[#57534E] hover:text-[#1C1917]" data-testid="back-to-docgen">
          ← Volver al generador
        </Link>
        <h1 className="hero-display text-4xl sm:text-5xl text-[#1C1917] mt-2">{wiz.title}</h1>
        <p className="text-sm text-[#57534E] mt-2 max-w-2xl">{wiz.description}</p>
        <div className="mt-2 text-xs uppercase tracking-widest font-bold text-[#57534E]">
          {wiz.estimated_minutes} min · {visibleSteps.length} pasos
        </div>

        {/* Progress bar */}
        <div
          className="mt-8 border-2 border-[#1C1917] h-3 bg-white"
          data-testid="wizard-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.min(100, progress)}
        >
          <div
            className="h-full bg-[#7C3AED] transition-all"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
        <div className="mt-2 text-xs uppercase tracking-widest font-bold text-[#1C1917]">
          {isReview ? "Revisión final" : `Paso ${stepIdx + 1} de ${visibleSteps.length}`}
        </div>

        {/* Step body */}
        {!isReview && currentStep && (
          <div
            className="mt-8 border-2 border-[#1C1917] bg-white p-8 shadow-[6px_6px_0_0_#1C1917] fade-up"
            key={currentStep.id}
            data-testid={`wizard-step-${currentStep.id}`}
          >
            <span className="eyebrow text-xs">{currentStep.title}</span>
            {currentStep.subtitle && (
              <p className="text-sm text-[#57534E] mt-1">{currentStep.subtitle}</p>
            )}
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {currentStep.questions.map((q) => (
                <div key={q.key} className={q.type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label className="eyebrow text-xs">
                    {q.label}{q.required ? " *" : ""}
                  </Label>
                  {q.help && (
                    <p className="text-[10px] text-[#57534E] mt-0.5">{q.help}</p>
                  )}
                  <div className="mt-1">
                    <Field q={q} value={answers[q.key]} onChange={setAnswer} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <Button
                onClick={prev}
                disabled={stepIdx === 0}
                variant="ghost"
                className="rounded-none font-black text-xs tracking-wider uppercase text-[#57534E] hover:bg-[#FFC53D] hover:text-[#1C1917]"
                data-testid="wiz-prev"
              >
                ← Anterior
              </Button>
              <Button
                onClick={next}
                className="rounded-none border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black tracking-wider shadow-[3px_3px_0_0_#1C1917] hover:shadow-[5px_5px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                data-testid="wiz-next"
              >
                {stepIdx === visibleSteps.length - 1 ? "Revisar →" : "Siguiente →"}
              </Button>
            </div>
          </div>
        )}

        {/* Review step */}
        {isReview && !result && (
          <div className="mt-8 border-2 border-[#1C1917] bg-white p-8 shadow-[6px_6px_0_0_#1C1917] fade-up" data-testid="wizard-review">
            <span className="eyebrow text-xs">Revisión final</span>
            <h2 className="display text-2xl mt-1 text-[#1C1917]">Listo para generar</h2>
            <p className="text-sm text-[#57534E] mt-2">
              Revisa tus respuestas. Si algo no es correcto, vuelve atrás. Cuando estés listo, elige el formato y genera.
            </p>
            <div className="mt-6 space-y-3 max-h-96 overflow-y-auto pr-2">
              {visibleSteps.map((s) => (
                <div key={s.id} className="border-2 border-[#1C1917] p-4 bg-[#FDFBF7]" data-testid={`review-${s.id}`}>
                  <div className="font-black text-sm text-[#1C1917] mb-2">{s.title}</div>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    {s.questions.map((q) => (
                      <div key={q.key} className="flex gap-2">
                        <dt className="text-[#57534E] font-bold uppercase tracking-wider">{q.label}:</dt>
                        <dd className="text-[#1C1917]">{answers[q.key] || "—"}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Label className="eyebrow text-xs">Formato de salida</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {FORMATS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setOutputFormat(f.id)}
                    className={`h-11 border-2 border-[#1C1917] text-xs font-black tracking-wider uppercase transition-all ${
                      outputFormat === f.id
                        ? "bg-[#1C1917] text-[#FDFBF7] shadow-[3px_3px_0_0_#FFC53D]"
                        : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
                    }`}
                    data-testid={`wiz-format-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between">
              <Button
                onClick={prev}
                variant="ghost"
                className="rounded-none font-black text-xs tracking-wider uppercase text-[#57534E] hover:bg-[#FFC53D] hover:text-[#1C1917]"
                data-testid="wiz-back-from-review"
              >
                ← Editar respuestas
              </Button>
              <Button
                onClick={generate}
                disabled={generating}
                className="rounded-none h-12 px-6 border-2 border-[#1C1917] bg-[#7C3AED] text-white hover:bg-[#6D28D9] font-black tracking-wider shadow-[4px_4px_0_0_#1C1917] hover:shadow-[6px_6px_0_0_#1C1917] hover:-translate-y-[1px] transition-all"
                data-testid="wiz-generate"
              >
                {generating ? "Generando con IA…" : "Generar documento"}
              </Button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-8 border-2 border-[#1C1917] bg-white p-8 shadow-[6px_6px_0_0_#1C1917] fade-up" data-testid="wizard-result">
            <span className="eyebrow text-xs">Documento listo</span>
            <h3 className="display text-2xl mt-2 text-[#1C1917]">{result.title}</h3>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {result.files.map((f) => (
                <div key={f.id} className="border-2 border-[#1C1917] bg-[#FDFBF7] p-4 flex items-center justify-between">
                  <div>
                    <div className="font-black text-sm text-[#1C1917] truncate max-w-[18ch]" title={f.original_filename}>
                      {f.original_filename}
                    </div>
                    <div className="text-xs text-[#57534E] uppercase">{f.ext} · {Math.round(f.size / 1024)} KB</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => downloadFile(f.id, f.original_filename)}
                      className="rounded-none h-9 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] hover:bg-black font-black text-xs"
                      data-testid={`wiz-download-${f.ext}`}
                    >
                      Descargar
                    </Button>
                    {f.ext === "pdf" && (
                      <Button
                        onClick={() => navigate(`/tools/pdf/sign?file=${f.id}`)}
                        className="rounded-none h-9 border-2 border-[#1C1917] bg-[#E5484D] text-white hover:bg-[#CE2C31] font-black text-xs"
                        data-testid="wiz-sign"
                      >
                        Firmar PDF
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Button
                onClick={() => { setResult(null); setStepIdx(0); setAnswers({}); }}
                variant="outline"
                className="rounded-none border-2 border-[#1C1917] bg-white text-[#1C1917] hover:bg-[#FFC53D] font-black text-xs"
                data-testid="wiz-restart"
              >
                Empezar otro
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
